import { ARCADE_FPS } from './common.js';
import type { ArcadeBaseState, ArcadeSimulation } from './common.js';
import { ArcadeRng } from './rng.js';

/**
 * Kueh Stack: build the tallest kueh lapis you can. A layer slides back and
 * forth; drop it onto the stack and whatever overhangs is sliced off. Land
 * it (nearly) perfectly and nothing is lost -- string perfects together and
 * the layer even grows back. Miss the stack entirely and it's over.
 */

export const KS_WIDTH = 160;
export const KS_HEIGHT = 200;
export const KS_LAYER_H = 8;
export const KS_BASE_W = 72;
export const KS_FRAMES = 90 * ARCADE_FPS;
/** Within this many pixels counts as a perfect drop. */
export const KS_PERFECT = 2;

export const KS_DROP = 1;

export interface KsLayer {
  x: number;
  w: number;
  perfect: boolean;
}

export interface KuehStackState extends ArcadeBaseState {
  rng: ArcadeRng;
  /** layers[0] is the plate. */
  layers: KsLayer[];
  /** The sliding layer: fixed-point x, direction and speed. */
  movingX: number;
  movingW: number;
  dir: 1 | -1;
  speed: number;
  perfectStreak: number;
  /** Frames left before another drop is accepted. */
  cooldown: number;
}

export const kuehStack: ArcadeSimulation<KuehStackState> = {
  maxFrames: KS_FRAMES,

  create(seed) {
    const s: KuehStackState = {
      frame: 0,
      over: false,
      score: 0,
      fx: [],
      rng: new ArcadeRng(seed),
      layers: [{ x: (KS_WIDTH - KS_BASE_W) / 2, w: KS_BASE_W, perfect: true }],
      movingX: 0,
      movingW: KS_BASE_W,
      dir: 1,
      speed: 256,
      perfectStreak: 0,
      cooldown: 0,
    };
    nextLayer(s);
    return s;
  },

  validCode(code) {
    return code === KS_DROP;
  },

  step(s, codes) {
    if (s.over) return;
    s.fx = [];
    if (s.cooldown > 0) s.cooldown -= 1;

    // Slide, bouncing off the walls.
    s.movingX += s.dir * s.speed;
    const maxX = (KS_WIDTH - s.movingW) * 256;
    if (s.movingX <= 0) {
      s.movingX = 0;
      s.dir = 1;
    } else if (s.movingX >= maxX) {
      s.movingX = maxX;
      s.dir = -1;
    }

    if (codes.includes(KS_DROP) && s.cooldown === 0) drop(s);

    s.frame += 1;
    if (s.frame >= KS_FRAMES) s.over = true;
  },
};

function drop(s: KuehStackState): void {
  s.cooldown = 8;
  const top = s.layers[s.layers.length - 1]!;
  const x = s.movingX >> 8;
  const left = Math.max(x, top.x);
  const right = Math.min(x + s.movingW, top.x + top.w);
  const overlap = right - left;
  const y = s.layers.length;

  if (overlap <= 0) {
    s.fx.push({ kind: 'fall', x, y });
    s.over = true;
    return;
  }

  if (Math.abs(x - top.x) <= KS_PERFECT) {
    s.perfectStreak += 1;
    let w = top.w;
    let lx = top.x;
    // Three perfects in a row win back a little width.
    if (s.perfectStreak >= 3 && w < KS_BASE_W) {
      w = Math.min(KS_BASE_W, w + 4);
      lx = Math.max(0, lx - 2);
    }
    s.layers.push({ x: lx, w, perfect: true });
    s.score += 20 + 5 * Math.min(s.perfectStreak, 10);
    s.fx.push({ kind: 'perfect', x: lx + (w >> 1), y, value: s.perfectStreak });
  } else {
    s.perfectStreak = 0;
    s.layers.push({ x: left, w: overlap, perfect: false });
    s.score += 10;
    // The sliced-off piece, for the falling-crumb effect.
    const cutX = x < top.x ? x : right;
    s.fx.push({ kind: 'trim', x: cutX, y, value: s.movingW - overlap });
  }
  nextLayer(s);
}

function nextLayer(s: KuehStackState): void {
  const top = s.layers[s.layers.length - 1]!;
  s.movingW = top.w;
  const fromLeft = s.layers.length % 2 === 1;
  s.dir = fromLeft ? 1 : -1;
  s.movingX = fromLeft ? 0 : (KS_WIDTH - s.movingW) * 256;
  s.speed = Math.min(1100, 256 + s.layers.length * 28 + s.rng.int(0, 48));
}
